import { SiteSiteTypeEnum, type CloudAccessConnection, type Site } from '@app/types/sdk';
import { inferSiteType } from './siteForm';

export type CloudProvider = string;

export type CloudAccessView = Partial<CloudAccessConnection> & {
  provider: CloudProvider;
  accountId: string;
  linkedSiteCount: number;
  linkedLocations: string[];
  status: string;
  type: string;
};

export type CloudAccessSetupAction =
  | { enabled: true; label: string }
  | { enabled: false; label: string; unavailableReason: string };

type CloudScope = {
  provider: CloudProvider;
  accountId: string;
  location: string;
};

type CloudAccessSite = Pick<
  Site,
  'siteType' | 'awsAccountId' | 'awsRegion' | 'azureSubscriptionId' | 'azureLocation'
>;

const normalizeProvider = (value?: string | null): string => value?.trim().toLowerCase() ?? '';

const connectionProvider = (connection: CloudAccessConnection): string =>
  normalizeProvider(connection.provider) || (connection.awsAccountId?.trim() ? 'aws' : '');

const connectionAccountId = (connection: CloudAccessConnection): string =>
  connection.accountId?.trim() || connection.awsAccountId?.trim() || '';

const cloudScopeForSite = (site: CloudAccessSite): CloudScope | null => {
  const siteType = inferSiteType(site);
  if (siteType === SiteSiteTypeEnum.AwsCloud) {
    const accountId = site.awsAccountId?.trim() ?? '';
    return accountId
      ? { provider: 'aws', accountId, location: site.awsRegion?.trim() ?? '' }
      : null;
  }
  if (siteType === SiteSiteTypeEnum.AzureCloud) {
    const accountId = site.azureSubscriptionId?.trim() ?? '';
    return accountId
      ? { provider: 'azure', accountId, location: site.azureLocation?.trim() ?? '' }
      : null;
  }
  return null;
};

export const cloudAccessRowKey = (row: Pick<CloudAccessView, 'provider' | 'accountId'>): string =>
  `${row.provider}:${row.accountId}`;

export const cloudProviderLabel = (provider: CloudProvider): string => {
  const normalized = normalizeProvider(provider);
  if (normalized === 'aws') {
    return 'AWS';
  }
  if (normalized === 'azure') {
    return 'Azure';
  }
  if (normalized === 'gcp') {
    return 'GCP';
  }
  return normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : 'Unknown';
};

export const cloudAccessSetupAction = (provider: CloudProvider, status?: string): CloudAccessSetupAction => {
  const providerLabel = cloudProviderLabel(provider);
  const normalized = normalizeProvider(provider);
  if (normalized === 'aws') {
    return { enabled: true, label: 'Open in AWS Console' };
  }
  if (normalized === 'azure') {
    const normalizedStatus = status?.trim().toLowerCase();
    return {
      enabled: true,
      label: normalizedStatus === 'pending' || normalizedStatus === 'enabled' ? 'Open Azure setup' : 'Connect Azure'
    };
  }
  return {
    enabled: false,
    label: `${providerLabel} setup unavailable`,
    unavailableReason: `${providerLabel} onboarding is not available yet.`
  };
};

export const buildCloudAccessRows = (
  sites: CloudAccessSite[],
  connections: CloudAccessConnection[],
  unavailableProviders: ReadonlySet<string> = new Set()
): CloudAccessView[] => {
  const summaries = new Map<string, { scope: CloudScope; linkedSiteCount: number; linkedLocations: Set<string> }>();

  sites.forEach((site) => {
    const scope = cloudScopeForSite(site);
    if (!scope) {
      return;
    }
    const key = `${scope.provider}:${scope.accountId}`;
    const current = summaries.get(key) ?? {
      scope,
      linkedSiteCount: 0,
      linkedLocations: new Set<string>()
    };
    current.linkedSiteCount += 1;
    if (scope.location) {
      current.linkedLocations.add(scope.location);
    }
    summaries.set(key, current);
  });

  const connectionsByScope = new Map<string, CloudAccessConnection>();
  connections.forEach((connection) => {
    const provider = connectionProvider(connection);
    const accountId = connectionAccountId(connection);
    if (provider && accountId) {
      connectionsByScope.set(`${provider}:${accountId}`, connection);
    }
  });

  return Array.from(summaries.values())
    .map(({ scope, linkedSiteCount, linkedLocations }) => {
      const stored = connectionsByScope.get(`${scope.provider}:${scope.accountId}`);
      return {
        ...(stored ?? {}),
        provider: scope.provider,
        accountId: scope.accountId,
        linkedSiteCount,
        linkedLocations: Array.from(linkedLocations).sort(),
        status: unavailableProviders.has(scope.provider) ? 'unknown' : stored?.status?.trim() || 'action_required',
        type: stored?.type?.trim() || ''
      };
    })
    .sort((a, b) => cloudAccessRowKey(a).localeCompare(cloudAccessRowKey(b)));
};
